import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataDisplay,
  Separator,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@folio/components';
import { cn } from '@folio/lib';
import { useInvoicingFrequencyLabels } from '@folio/constants';
import { cva } from 'class-variance-authority';
import { type CellType, useKorintProductConfig, type PriceDisplayConfig } from 'korint-sdk/react';
import { useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { isLineVisibleForRole, useFirmType } from '@folio/hooks';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import {
  getTotals,
  getQuoteLineTranslationKeys,
  getTooltipKeys,
  getVariantByKey,
  resolvePresetAmount,
} from '@folio/utils/price-display-utils';
import { QuoteContext } from '../shared-quote/QuoteContext';
import { useTranslation } from 'react-i18next';
import { InfoIcon } from 'lucide-react';
import type { PriceBreakdownLineOutputDto } from 'korint-sdk';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

const groupVariants = cva('', {
  variants: {
    variant: {
      callout: 'rounded-lg p-4 flex flex-col bg-primary/5',
    },
  },
  defaultVariants: {},
});

const lineVariants = cva('flex flex-row text-sm justify-between', {
  variants: {
    variant: {
      bold: 'font-bold',
      dimmed: 'text-gray-600',
    },
  },
  defaultVariants: {},
});

function DisplayLine({
  label,
  amount,
  type,
  tooltip,
  variant,
  frequencyLabel,
  isLoading,
}: Readonly<{
  label: string;
  amount: number;
  type?: CellType;
  tooltip?: string;
  variant?: 'bold' | 'dimmed';
  frequencyLabel?: string;
  isLoading?: boolean;
}>) {
  const displayType: CellType = type || 'price';

  return (
    <div aria-label={label} className={lineVariants({ variant })}>
      <Group gap={1}>
        <span>{label}</span>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="h-3.5 w-3.5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        )}
      </Group>
      <Group align="baseline" gap={1}>
        {isLoading ? (
          <Skeleton className="h-4 w-20 inline-flex" />
        ) : (
          <DataDisplay type={displayType} value={amount} />
        )}
        {frequencyLabel && (
          <Text inline weight="normal" size="sm" className="text-gray-400">
            / {frequencyLabel}
          </Text>
        )}
      </Group>
    </div>
  );
}

type ResolvedLine = {
  key: string;
  label: string;
  amount: number;
  type?: CellType;
  tooltip?: string;
  variant?: 'bold' | 'dimmed';
  subLines?: ResolvedLine[];
};

type ResolvedGroup = {
  variant?: 'callout';
  lines: ResolvedLine[];
};

type QuoteRecapModuleProps = {
  /** Recap-only label overrides per PriceBreakdown key — keeps the dashboard
   * premium card's verbose i18n labels untouched while the funnel recap can
   * render short product-specific labels. */
  labelOverrides?: Record<string, string>;
};

export function QuoteRecapModule({ labelOverrides }: QuoteRecapModuleProps = {}) {
  const { t } = useTranslation();
  const invoicingFrequencyLabels = useInvoicingFrequencyLabels();
  const resolveLabel = (key: string): string =>
    labelOverrides?.[key] ?? t(getQuoteLineTranslationKeys(policy.productId, key));

  const form = useFormContext();
  const { policy } = usePolicyOnDashboard();
  const { calculatedQuotes, isLoading } = useContext(QuoteContext);

  const { productsConfig } = useKorintProductConfig();
  const priceBreakdownConfig = productsConfig[policy.productId]?.priceBreakdownConfig;

  const { firmType } = useFirmType();

  const insurer = form.watch('insurer');
  const tier = form.watch('tier');

  const breakdown = calculatedQuotes.find(q => q.tier === tier && q.insurer === insurer)?.breakdown;

  const invoicingFrequency = form.watch('invoicingConfig.frequency');
  const effectiveFrequency = invoicingFrequency ?? breakdown?.frequency;

  // `amount` is the contract-total; `adjustedAmount` is the per-period figure we want to show.
  const displayAmount = (line: { amount: number; adjustedAmount?: number }): number =>
    line.adjustedAmount ?? line.amount;

  const resolveSubLines = (
    backendLine: PriceBreakdownLineOutputDto,
  ): ResolvedLine[] | undefined => {
    if (!backendLine.subLines?.length) return undefined;
    return backendLine.subLines
      .filter(l => isLineVisibleForRole(l, firmType))
      .map(sub => ({
        key: sub.key,
        label: resolveLabel(sub.key),
        amount: displayAmount(sub),
        type: sub.type,
        variant: 'dimmed' as const,
      }));
  };

  const groups = useMemo((): ResolvedGroup[] => {
    if (!breakdown) return [];

    const configGroups = priceBreakdownConfig?.quoteRecapOverride ?? priceBreakdownConfig?.groups;

    if (configGroups) {
      const effectiveConfig = { groups: configGroups } as PriceDisplayConfig;
      const variantByKey = getVariantByKey(effectiveConfig);
      const tooltipKeys = getTooltipKeys(effectiveConfig);

      const resolveTooltip = (key: string) =>
        tooltipKeys.has(key)
          ? t(getQuoteLineTranslationKeys(policy.productId, `${key}_tooltip`), {
              defaultValue: '',
            }) || undefined
          : undefined;

      const linesByKey = new Map<string, PriceBreakdownLineOutputDto[]>();
      for (const l of breakdown.lines ?? []) {
        const group = linesByKey.get(l.key);
        if (group) group.push(l);
        else linesByKey.set(l.key, [l]);
      }

      return configGroups
        .map(group => {
          const resolvedLines: ResolvedLine[] = [];
          for (const configLine of group.lines) {
            if (configLine.type === 'PRESET') {
              const amount = resolvePresetAmount(breakdown, configLine.key);
              resolvedLines.push({
                key: configLine.key,
                label: t(getQuoteLineTranslationKeys(policy.productId, configLine.key)),
                amount,
                tooltip: resolveTooltip(configLine.key),
                variant: variantByKey.get(configLine.key),
                subLines: configLine.subLines?.flatMap(sub => {
                  const subAmount = resolvePresetAmount(breakdown, sub.key);
                  if (subAmount == null) return [];
                  return [
                    {
                      key: sub.key,
                      label: t(getQuoteLineTranslationKeys(policy.productId, sub.key as string)),
                      amount: subAmount,
                      variant: sub.variant,
                    },
                  ];
                }),
              });
              continue;
            }
            const candidates = linesByKey.get(configLine.key) ?? [];
            const backendLine =
              candidates.find(c => isLineVisibleForRole(c, firmType)) ?? candidates[0];
            const effectiveAmount = backendLine?.adjustedAmount;
            if (!backendLine || !isLineVisibleForRole(backendLine, firmType) || !effectiveAmount)
              continue;
            resolvedLines.push({
              key: backendLine.key,
              label: resolveLabel(backendLine.key),
              amount: displayAmount(backendLine),
              type: backendLine.type,
              tooltip: resolveTooltip(backendLine.key),
              variant: variantByKey.get(backendLine.key),
              subLines: resolveSubLines(backendLine),
            });
          }
          return { variant: group.variant, lines: resolvedLines };
        })
        .filter(group => group.lines.length > 0);
    }

    return [
      {
        lines: [
          {
            key: 'PRE_TAXES',
            label: t('QuoteRecapCard.preTaxes'),
            amount:
              (breakdown.recurring?.withFeesAndTaxes ?? 0) - (breakdown.recurring?.taxes ?? 0),
            type: 'price',
          },
          {
            key: 'TAXES',
            label: t('QuoteRecapCard.taxes'),
            amount: breakdown.recurring?.taxes ?? 0,
            type: 'price',
          },
        ],
      },
    ];
  }, [breakdown, priceBreakdownConfig, firmType, t]);

  const totals = useMemo(
    () => (breakdown ? getTotals(breakdown, priceBreakdownConfig) : []),
    [breakdown, priceBreakdownConfig],
  );

  const hasLines = groups.some(g => g.lines.length > 0);

  if (!hasLines && !totals.length) {
    return null;
  }

  return (
    <Card className="sticky top-4">
      <CardHeader>
        <CardTitle>{t('QuoteRecapCard.title')}</CardTitle>
        {priceBreakdownConfig?.description && (
          <div className={cn('text-sm text-muted-foreground whitespace-pre-wrap')}>
            {priceBreakdownConfig.description}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {groups.map(({ variant, lines }, groupIndex) => (
          <div key={`${groupIndex}`}>
            <div className={groupVariants({ variant })}>
              {lines.map(line => (
                <Stack key={line.key} className="mb-2">
                  <DisplayLine
                    label={line.label}
                    amount={line.amount}
                    type={line.type}
                    tooltip={line.tooltip}
                    variant={line.variant}
                    isLoading={isLoading}
                  />
                  {line.subLines && line.subLines.length > 0 && (
                    <Stack gap={0} className="ml-4 mt-1">
                      {line.subLines.map(sub => (
                        <DisplayLine
                          key={sub.key}
                          label={sub.label}
                          amount={sub.amount}
                          type={sub.type}
                          tooltip={sub.tooltip}
                          variant={sub.variant}
                          isLoading={isLoading}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              ))}
            </div>
            {groupIndex < groups.length - 1 && <Separator className="my-4" />}
          </div>
        ))}
        {hasLines && totals.length > 0 && <Separator className="my-4" />}
        {totals.length > 0 && (
          <Stack gap={0}>
            {totals.map(total => (
              <Stack key={total.key} className="mb-2">
                <DisplayLine
                  label={resolveLabel(total.key)}
                  amount={total.amount}
                  variant={total.key === 'TOTAL' ? 'bold' : undefined}
                  isLoading={isLoading}
                  frequencyLabel={
                    effectiveFrequency && effectiveFrequency in invoicingFrequencyLabels
                      ? invoicingFrequencyLabels[effectiveFrequency]
                      : undefined
                  }
                />
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
